import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { date } from 'quasar'
import {
  StaffDashboardPeriod,
  StaffMediaProblemKind,
  type StaffDashboardSummaryResponse,
  type MediaPipelineStage
} from '@roadfootage/api-client'
import { formatDuration, formatRatio } from '@/utils/format'
import { STEP_LABELS } from '@/utils/pipeline-stage'
import { useRestData } from '../../composables/useRestData'
import { useApi } from '../../composables/api'
import logger from '../../logger'

export interface DashboardMetric {
  label: string
  /** null while there is no summary (not loaded, or the call failed): the UI shows a dash. */
  count: number | null
  /** Shown instead of the formatted count (a duration, a ratio). Ignored while `count` is null. */
  display?: string
  /** Secondary line, e.g. "+1 new in 24h". */
  sub?: string
  icon: string
  /** Router name of the filtered list this tile opens. Absent when there is no such list. */
  route?: string
  query?: Record<string, string>
  color?: string
}

/** One row of "Recent problems": a failed or threat file, with where to open it. */
export interface MediaProblemRow {
  fileId: string
  fileName: string
  kind: 'failed' | 'threat'
  /** Stage label, or null for a file processed before progress was tracked. */
  stage: string | null
  occurredAt: Date
  route: string
  query: Record<string, string>
}

export const PERIOD_LABELS: Record<StaffDashboardPeriod, string> = {
  [StaffDashboardPeriod.Day]: '24h',
  [StaffDashboardPeriod.Week]: '7d',
  [StaffDashboardPeriod.Month]: '30d'
}

export const useDashboardStore = defineStore('dashboard', () => {
  const { adminUserProfilesApi, adminIncidentReportsApi, adminDashboardApi } = useApi()

  // --- Summary State ---
  const period = ref<StaffDashboardPeriod>(StaffDashboardPeriod.Day)
  const summary = ref<StaffDashboardSummaryResponse | null>(null)
  const metricsLoading = ref(false)
  /** True when the last summary call failed, so the media section can say so. */
  const summaryFailed = ref(false)
  let latestSummaryRequest = 0

  // --- Grid Composables (Internal) ---
  const usersGrid = useRestData<any>((p) => adminUserProfilesApi.adminUserProfilesPaginate(p), {
    initialPageSize: 10,
    defaultSort: [{ sortBy: 'createdAt', descending: true }]
  })

  const incidentsGrid = useRestData<any>(
    (p) => adminIncidentReportsApi.adminIncidentReportsPaginate(p),
    {
      initialPageSize: 10,
      defaultSort: [{ sortBy: 'createdAt', descending: true }]
    }
  )

  // --- Computed Aliases (To keep existing component logic working) ---
  const recentUsers = computed(() => usersGrid.items.value)
  const recentIncidents = computed(() => incidentsGrid.items.value)
  const loading = computed(
    () => metricsLoading.value || usersGrid.isLoading.value || incidentsGrid.isLoading.value
  )

  const metrics = computed<DashboardMetric[]>(() => {
    const label = PERIOD_LABELS[period.value]
    const platform = summary.value?.platform
    const sinceQuery: Record<string, string> = summary.value?.since
      ? { createdAfter: summary.value.since.toISOString() }
      : {}
    const backlogSub = (metric?: { newInPeriod?: number }) =>
      metric ? `+${metric.newInPeriod ?? 0} new in ${label}` : undefined

    const tiles: DashboardMetric[] = [
      {
        label: `New users (${label})`,
        count: platform?.newUsers ?? null,
        icon: 'mdi-account-plus',
        route: 'users',
        query: sinceQuery
      },
      {
        label: `New incident reports (${label})`,
        count: platform?.newIncidentReports ?? null,
        icon: 'mdi-alert-decagram',
        route: 'incident-reports',
        query: sinceQuery
      },
      {
        label: 'Pending moderation',
        count: platform?.pendingModeration?.total ?? null,
        sub: backlogSub(platform?.pendingModeration),
        icon: 'mdi-gavel',
        route: 'incident-reports',
        query: { moderationStatus: 'Pending' },
        color: 'orange-9'
      },
      {
        label: 'Open bounties',
        count: platform?.openBounties?.total ?? null,
        sub: backlogSub(platform?.openBounties),
        icon: 'mdi-star-circle',
        route: 'moderation-bounties',
        query: { open: '1' }
      },
      {
        label: 'Pending takedowns',
        count: platform?.pendingTakedowns?.total ?? null,
        sub: backlogSub(platform?.pendingTakedowns),
        icon: 'mdi-shield-off'
      }
    ]
    return tiles
  })

  const mediaTiles = computed<DashboardMetric[]>(() => {
    const noQuery: Record<string, string> = {}
    const label = PERIOD_LABELS[period.value]
    const media = summary.value?.media
    const samples = (n?: number) =>
      media ? `from ${n ?? 0} video${n === 1 ? '' : 's'}` : undefined
    const stuck = media?.stuck ?? null

    return [
      {
        label: 'Queued',
        count: media?.queued ?? null,
        icon: 'mdi-tray-full',
        route: 'files',
        query: { attention: 'Queued' }
      },
      {
        label: 'Processing',
        count: media?.processing ?? null,
        icon: 'mdi-cog-sync',
        route: 'files',
        query: { attention: 'Processing' }
      },
      {
        label: 'Stuck',
        count: stuck,
        sub: media ? `processing > ${media.stuckAfterMinutes ?? 0} min` : undefined,
        icon: 'mdi-timer-sand',
        route: 'files',
        query: { attention: 'Processing' },
        color: stuck !== null && stuck > 0 ? 'orange-9' : undefined
      },
      {
        label: `Failed (${label})`,
        count: media?.failedInPeriod ?? null,
        icon: 'mdi-alert',
        route: 'files',
        query: { attention: 'Failed' }
      },
      {
        label: `Threats (${label})`,
        count: media?.threatsInPeriod ?? null,
        icon: 'mdi-shield-bug',
        route: 'files',
        query: { attention: 'Threat' }
      },
      {
        label: `Median time (${label})`,
        count: media?.medianProcessingSeconds ?? null,
        display: formatDuration(media?.medianProcessingSeconds),
        sub: samples(media?.processingSampleSize),
        icon: 'mdi-clock-outline',
        route: 'files',
        query: noQuery
      },
      {
        label: `Size ratio (${label})`,
        count: media?.medianSizeRatio ?? null,
        display: formatRatio(media?.medianSizeRatio),
        sub: samples(media?.sizeRatioSampleSize),
        icon: 'mdi-percent-outline',
        route: 'files',
        query: noQuery
      }
    ]
  })

  const recentProblems = computed<MediaProblemRow[]>(() =>
    (summary.value?.media?.recentProblems ?? []).map((p) => ({
      fileId: p.fileId ?? '',
      fileName: p.fileName ?? '',
      kind: p.kind === StaffMediaProblemKind.Threat ? 'threat' : 'failed',
      stage: p.stage ? STEP_LABELS[p.stage as MediaPipelineStage] : null,
      occurredAt: p.occurredAt ?? new Date(0),
      route: p.incidentReportId ? 'incident-reports' : 'files',
      query: { id: p.incidentReportId ?? p.fileId ?? '' }
    }))
  )

  // --- Actions ---

  async function loadSummary() {
    const ticket = ++latestSummaryRequest
    metricsLoading.value = true
    try {
      const result = await adminDashboardApi.adminDashboardGetSummary({ period: period.value })
      if (ticket === latestSummaryRequest) {
        summary.value = result
        summaryFailed.value = false
      }
    } catch (error) {
      logger.error({ error }, 'dashboard summary error')
      if (ticket === latestSummaryRequest) {
        summary.value = null
        summaryFailed.value = true
      }
    } finally {
      if (ticket === latestSummaryRequest) metricsLoading.value = false
    }
  }

  const fetchDashboardSummary = async () => {
    const isoTS = date.subtractFromDate(new Date(), { hours: 24 }).toISOString()

    // Recent tables stay on a 24h rolling window whatever the tile period.
    usersGrid.activeFilters.value['createdAt'] = { v: isoTS, op: 'gt' }
    incidentsGrid.activeFilters.value['createdAt'] = { v: isoTS, op: 'gt' }

    await Promise.all([loadSummary(), usersGrid.fetch(), incidentsGrid.fetch()])
  }

  async function setPeriod(next: StaffDashboardPeriod) {
    period.value = next
    await loadSummary()
  }

  return {
    // State/Computed
    period,
    summary,
    metrics,
    mediaTiles,
    recentProblems,
    summaryFailed,
    recentUsers,
    recentIncidents,
    loading,

    // Grids (Exposed for pagination/sorting in the UI)
    usersGrid,
    incidentsGrid,

    // Actions
    fetchDashboardSummary,
    setPeriod
  }
})
